import {
  HelpCircle,
  Mic,
  Languages,
  Mic2,
  Sparkles,
  Type,
  AlertCircle,
  Settings2,
} from 'lucide-react';
import { Combobox } from '@/components/ui/combobox';
import { SwitchWithLabel } from '@/components/ui/switch-with-label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { BadgeMultiSelect } from '@/components/ui/badge-multi-select';
import { Skeleton } from '@/components/ui/skeleton';
import { NumberInput } from '@/components/ui/number-input';
import type { Assistant, AssistantTranscriber, LanguageOption } from '@/api/assistants/assistants.types';
import { defaultAssistantConfig } from '@/api/assistants/defaultConfig';
import { getAssistantLanguages } from '@/api/assistants/assistants.api';
import { Input } from '@/components/ui/input';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/context/TenantContext';

interface TranscriberTabProps {
  assistant: Omit<Assistant, 'name'>;
  onUpdate: (updates: Partial<Assistant>) => void;
}

interface KeywordOption {
  value: string;
  label: string;
  intensifier: number;
}

export function TranscriberTab({ assistant, onUpdate }: TranscriberTabProps) {
  const { branding } = useTenant();
  const transcriber = assistant?.transcriber || defaultAssistantConfig.transcriber;
  
  // State for language options
  const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>([]);
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(true);
  
  // Fetch language options on component mount
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        setIsLoadingLanguages(true);
        const response = await getAssistantLanguages();
        if (response.data) {
          setLanguageOptions(response.data);
        } else if (response.error) {
          console.error('Failed to fetch languages:', response.error);
          // Fallback to default options if API fails
          setLanguageOptions([
            { value: 'en', label: 'English' },
            { value: 'ar-AE', label: 'Arabic' }
          ]);
        }
      } catch (error) {
        console.error('Error fetching languages:', error);
        // Fallback to default options if API fails
        setLanguageOptions([
          { value: 'en', label: 'English' },
          { value: 'ar-AE', label: 'Arabic' }
        ]);
      } finally {
        setIsLoadingLanguages(false);
      }
    };
    
    fetchLanguages();
  }, []);

  const handleUpdate = (key: keyof AssistantTranscriber, value: AssistantTranscriber[keyof AssistantTranscriber]) => {
    let updatedTranscriber = {
      ...assistant.transcriber,
      [key]: value
    };

    // For Arabic language, exclude unsupported fields from the payload
    if (updatedTranscriber.language === 'ar-AE') {
      const { keywords, smartFormat, ...arabicTranscriber } = updatedTranscriber;
      updatedTranscriber = arabicTranscriber as any; // Type assertion needed as we're removing required fields for Arabic
    }

    onUpdate({
      transcriber: updatedTranscriber
    });
  };

  const [newKeyword, setNewKeyword] = React.useState('');
  const [newIntensifier, setNewIntensifier] = React.useState(1);

  const handleAddKeyword = () => {
    if (newKeyword.trim()) {
      const keyword = `${newKeyword}:${newIntensifier}`;
      handleUpdate('keywords', [...(transcriber.keywords || []), keyword]);
      setNewKeyword('');
      setNewIntensifier(1);
    }
  };

  const parseKeywords = (keywords: string[] = []): KeywordOption[] => {
    return keywords.map(k => {
      const [word, intensifier] = k.split(':');
      return {
        value: k,
        label: `${word} (${intensifier || '1'}×)`,
        intensifier: Number(intensifier) || 1
      };
    });
  };

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Left Column - Main Configuration */}
      <div className="col-span-8 space-y-6">
        {/* Transcription Settings Card */}
        <Card className="shadow-sm">
          <CardHeader className="border-b bg-muted/20">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <Mic className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 space-y-1">
                <CardTitle>Transcription Settings</CardTitle>
                <CardDescription>Configure speech-to-text language and accuracy</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Language Selection */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Languages className="h-4 w-4 text-primary" />
                <Label>Language</Label>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-sm">
                      <p>Select the primary language for transcription</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {isLoadingLanguages ? (
                <Skeleton className="h-9 w-full" />
              ) : languageOptions.length > 0 ? (
                <Combobox
                  options={languageOptions.map(option => ({
                    value: option.value,
                    label: option.label
                  }))}
                  value={transcriber.language}
                  onValueChange={(value) => {
                    // Update language and model based on selection
                    let updatedTranscriber = {
                      ...assistant.transcriber,
                      language: value,
                    };

                    // For Arabic language, exclude unsupported fields from the payload
                    if (value === 'ar-AE') {
                      const { keywords, smartFormat, ...arabicTranscriber } = updatedTranscriber;
                      updatedTranscriber = arabicTranscriber as any; // Type assertion needed as we're removing required fields for Arabic
                    }

                    onUpdate({
                      transcriber: updatedTranscriber
                    });
                  }}
                  placeholder="Select language..."
                  searchPlaceholder="Search languages..."
                  emptyMessage="No language found."
                />
              ) : (
                <div className="flex items-center text-destructive gap-2">
                  <AlertCircle className="h-4 w-4" />
                  <span className="text-sm">Failed to load languages</span>
                </div>
              )}
            </div>

          </CardContent>
        </Card>

        {/* Keywords Boost Card */}
        {transcriber.language !== 'ar-AE' && (
          <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 space-y-1">
                  <CardTitle>Keywords Boost</CardTitle>
                  <CardDescription>Improve recognition of specific terms and names</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <Label>Add Keywords</Label>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger>
                          <HelpCircle className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-md space-y-2">
                          <p><strong>What are Keywords?</strong></p>
                          <p>Keywords help improve transcription accuracy for specific terms like:</p>
                          <ul className="list-disc pl-4">
                            <li>Proper nouns (names, places)</li>
                            <li>Product names</li>
                            <li>Industry-specific terms</li>
                          </ul>
                          <p><strong>How Intensifiers Work:</strong></p>
                          <ul className="list-disc pl-4">
                            <li>Default: 1 (normal boost)</li>
                            <li>Higher values (e.g., 2, 3) = stronger boost</li>
                            <li>Maximum: 100 keywords per request</li>
                          </ul>
                          <p><strong>Best Practices:</strong></p>
                          <ul className="list-disc pl-4">
                            <li>Use for uncommon words only</li>
                            <li>Start with low intensifiers (1-2)</li>
                            <li>Avoid common words or phrases</li>
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Boost recognition of specific words in the transcription</p>
                </div>

                <div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1 space-y-2">
                      <Label>Keyword</Label>
                      <Input
                        placeholder="Enter word to boost (e.g., product name)"
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        className="h-9"
                      />
                    </div>
                    <div className="w-32 space-y-2">
                      <div className="flex items-center gap-1">
                        <Label className="text-sm">Boost Factor</Label>
                        <TooltipProvider delayDuration={200}>
                          <Tooltip>
                            <TooltipTrigger>
                              <HelpCircle className="h-3.5 w-3.5 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              <p className="text-xs font-semibold mb-1">Examples:</p>
                              <ul className="list-disc pl-4 text-xs space-y-0.5">
                                <li>1 = Normal boost</li>
                                <li>2 = Double boost</li>
                                <li>3 = Triple boost</li>
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      <NumberInput
                        value={newIntensifier}
                        onValueChange={(value) => setNewIntensifier(value || 1)}
                        min={1}
                        max={5}
                        step={1}
                        stepper={1}
                      />
                    </div>
                    <Button
                      onClick={handleAddKeyword}
                      disabled={!newKeyword.trim()}
                      size="sm"
                      className="h-9"
                    >
                      Add
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Example: "{branding?.company_name || 'Company'}:2" will make the transcriber twice as likely to recognize "{branding?.company_name || 'Company'}"
                  </p>
                </div>

                {(transcriber.keywords || []).length > 0 && (
                  <div className="rounded-md border p-4 bg-muted/30">
                    <div className="text-sm font-medium mb-3">Active Keywords</div>
                    <BadgeMultiSelect
                      options={parseKeywords(transcriber.keywords || [])}
                      value={transcriber.keywords || []}
                      onChange={(value) => handleUpdate('keywords', value)}
                      placeholder="No keywords added yet..."
                    />
                    <p className="text-xs text-muted-foreground mt-2">Click × to remove. Numbers in parentheses show boost strength.</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Right Column - Features */}
      <div className="col-span-4 space-y-6">
        {/* Transcription Features Card */}
        <Card className="shadow-sm">
          <CardHeader className="border-b bg-muted/20">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                <Settings2 className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 space-y-1">
                <CardTitle>Features</CardTitle>
                <CardDescription>Enable advanced transcription capabilities</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              {/* Background Denoising */}
              <SwitchWithLabel
                label="Background Denoising"
                icon={Mic2}
                subline="Filter background noise while the user is talking."
                checked={assistant.backgroundDenoisingEnabled || false}
                onCheckedChange={(checked) => onUpdate({ backgroundDenoisingEnabled: checked })}
              />

              {/* Smart Formatting */}
              {transcriber.language !== 'ar-AE' && (
                <SwitchWithLabel
                  icon={Type}
                  label="Smart Formatting"
                  subline="Automatically format numbers, dates, and other common patterns"
                  checked={transcriber.smartFormat}
                  onCheckedChange={(checked) => handleUpdate('smartFormat', checked)}
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}